import React from "react";
import { SelectedOddDisplay } from "../strainght/SelectedOddDisplay.tsx";
import { useBetContext } from "../../../../context/BetContext.tsx";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { BetDetails } from "../../../../models/MyBetModel.tsx";
import { Schedule } from "../../../../models/Schedule.tsx";
import {
    findPropsConflict,
    PROPS_CONFLICT_MESSAGES,
    toPropsConflictSubjectFromBet,
    toPropsConflictSubjectFromOdd,
} from "../../../../helpers/propsBetConflicts.ts";
import {
    getMarketConflictGroup,
    isParlayMainlineOddBlocked,
} from "../../../../helpers/parlayMarketConflicts.ts";
import { filterPreferredSportsbookOdds } from "../../../../helpers/functions.tsx";


interface IDataGamesComponents {
    marketData: Schedule[],
    showLimit: Record<string, number>;
    setShowLimit: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    marketId: string;
    onRequestBetsModal: (
        bet?: Partial<BetDetails>,
        openModal?: boolean
    ) => void;
    isSpecial?: boolean;
    propsLive?: boolean;
    enforcePropsConflicts?: boolean;
    fixtureId?: string | null;
    gameId?: string | null;
    bets?: Array<{ id_selection?: string; odd?: Odd; fixture_id?: string } & Partial<BetDetails>>;
    OddDisplay?: React.ComponentType<{
        o: Odd;
        onRequestBetsModal: Function;
        currentBet?: any;
        isSpecial?: boolean;
        propsLive?: boolean;
        disabled?: boolean;
        disabledReason?: string;
    }>;
}
export function DataGames(props: IDataGamesComponents) {

    const { bets: contextBets } = useBetContext();
    const {
        marketData, showLimit, marketId, setShowLimit, onRequestBetsModal,
        isSpecial, propsLive, enforcePropsConflicts = false, fixtureId, gameId,
    } = props;
    const bets = props.bets ?? contextBets;
    const OddDisplay = props.OddDisplay ?? SelectedOddDisplay;

    const selectedSubjects = enforcePropsConflicts
        ? (bets as BetDetails[])
            .filter(b => b?.id_selection || b?.odd?.id)
            .map(toPropsConflictSubjectFromBet)
        : [];

    const getConflictState = (odd: Odd, scheduleGameId?: string) => {
        if (!enforcePropsConflicts || (selectedSubjects.length === 0 && bets.length === 0)) {
            return { disabled: false, reason: undefined as string | undefined };
        }

        if (bets.some(b => b.id_selection === odd.id)) {
            return { disabled: false, reason: undefined };
        }

        const resolvedFixtureId = fixtureId || scheduleGameId;
        const resolvedGameId = gameId || fixtureId || scheduleGameId;

        // Mainline (ML / spread / totals): props blocks the whole conflict group (no replace)
        if (getMarketConflictGroup(odd.market_id)) {
            const blocked = isParlayMainlineOddBlocked(
                odd,
                bets,
                { gameId: resolvedGameId, fixtureId: resolvedFixtureId },
                { strictReject: true }
            );
            if (blocked) {
                return {
                    disabled: true,
                    reason: 'A selection in this market group is already in your slip for this game.',
                };
            }
            return { disabled: false, reason: undefined };
        }

        // Player / specialty props: disable other outcomes in the same market/subject
        const candidate = toPropsConflictSubjectFromOdd(odd, resolvedFixtureId);
        const hit = findPropsConflict(candidate, selectedSubjects);
        if (!hit) return { disabled: false, reason: undefined };
        return {
            disabled: true,
            reason: PROPS_CONFLICT_MESSAGES[hit.reason],
        };
    };

    

    return (
        <>
            {marketData.length === 0 && (
                <div className="no-odds-message">No odds available</div>
            )}

            {marketData.map(game => {

                const odds = game.odds ?? [];
                const filtered = (() => {

                    const marketOdds = odds.filter(
                        o => o.market_id === marketId
                    );
                
                    if (marketOdds.length === 0) return [];

                    return filterPreferredSportsbookOdds(marketOdds)
                        .sort((a, b) => {
                            const yesNoOrder = (odd: Odd) => {
                                const line = (odd.selection_line || '').toLowerCase().trim();
                                const label = `${odd.selection || ''} ${odd.name || ''} ${odd.normalized_selection || ''}`.toLowerCase();

                                if (line.includes('over') || line === 'yes' || /\byes\b/.test(label)) return 0;
                                if (line.includes('under') || line === 'no' || /\bno\b/.test(label)) return 1;
                                return 2;
                            };

                            const baseName = (odd: Odd) =>
                                (odd.name || odd.selection || '')
                                    .replace(/\s*\b(yes|no)\b\s*$/i, '')
                                    .trim()
                                    .toLowerCase();

                            const nameDiff = baseName(a).localeCompare(baseName(b));
                            if (nameDiff !== 0) return nameDiff;

                            const orderDiff = yesNoOrder(a) - yesNoOrder(b);
                            if (orderDiff !== 0) return orderDiff;

                            return (a.name || '').localeCompare(b.name || '');
                        });
                
                })();

                const groups = filtered.reduce((acc, odd) => {
                    if (!acc[odd.grouping_key]) acc[odd.grouping_key] = [];
                    acc[odd.grouping_key].push(odd);
                    return acc;
                }, {} as Record<string, Odd[]>);

                const groupEntries = Object.entries(groups);
                const groupLimitKey = `${game.id}-${marketId}`;
                const groupLimit = showLimit[groupLimitKey] ?? 5;

                if (odds.length === 0) {
                    return (
                        <div
                            key={`no-odds-${game.id ?? game.game_id}`}
                            className="no-odds-message"
                        >
                            No odds available
                        </div>
                    );
                }

                return (
                    <React.Fragment key={game.id}>

                        {groupEntries.slice(0, groupLimit).map(([key, list]) => {

                            const isTeamTotalLike = key.includes(":") && !key.startsWith("default");
                            let printedHeader = false;
                            const specialLimitKey = `${game.id}-${marketId}-${key}`;
                            const specialLimit = showLimit[specialLimitKey] ?? 5;

                            if (isTeamTotalLike) {

                                const overs = list.filter(o =>
                                    o.selection_line.toLowerCase().startsWith("over")
                                );

                                const unders = list.filter(o =>
                                    o.selection_line.toLowerCase().startsWith("under")
                                );

                                return (
                                    <div key={key} className={isSpecial ? "container-special-item" : ""}>
                                        {!isSpecial && !printedHeader && (
                                            <div className="header-over-under">
                                                <div>Over</div>
                                                <div>Under</div>
                                            </div>
                                        )}

                                        {!isSpecial && (() => {
                                            printedHeader = true;
                                            return null;
                                        })()}

                                        <div className={isSpecial ? "odd-special-item" : `props-data-container${propsLive ? " props-live-row" : ""}`}>
                                            <div className={isSpecial ? "team-future" : ""}>
                                                <span className="name-odd-props">
                                                    {list[0].selection.split("::")[0]}
                                                </span>
                                            </div>

                                            <div
                                                className={propsLive ? "props-live-odds-grid" : ""}
                                                style={isSpecial
                                                    ? { width: "25%", display: "contents" }
                                                    : propsLive
                                                        ? undefined
                                                        : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }
                                                }
                                            >
                                                <div className={isSpecial ? "" : "props-odd-container"} style={isSpecial ? { width: "38%" } : undefined}>
                                                    {(isSpecial ? overs.slice(0, specialLimit) : overs)
                                                        .map(odd => {
                                                            const bet = bets?.find(b => b.id_selection === odd.id);
                                                            const conflict = getConflictState(odd, game.id);

                                                            return (
                                                                <OddDisplay
                                                                    key={odd.id}
                                                                    o={odd}
                                                                    currentBet={bet}
                                                                    onRequestBetsModal={onRequestBetsModal}
                                                                    isSpecial={isSpecial}
                                                                    propsLive={propsLive}
                                                                    disabled={conflict.disabled}
                                                                    disabledReason={conflict.reason}
                                                                />
                                                            );
                                                        })}
                                                </div>

                                                <div className={isSpecial ? "" : "props-odd-container"} style={isSpecial ? { width: "35%" } : undefined}>
                                                    {(isSpecial ? unders.slice(0, specialLimit) : unders)
                                                        .map(odd => {
                                                            const bet = bets?.find(b => b.id_selection === odd.id);
                                                            const conflict = getConflictState(odd, game.id);

                                                            return (
                                                                <OddDisplay
                                                                    key={odd.id}
                                                                    o={odd}
                                                                    currentBet={bet}
                                                                    onRequestBetsModal={onRequestBetsModal}
                                                                    isSpecial={isSpecial}
                                                                    propsLive={propsLive}
                                                                    disabled={conflict.disabled}
                                                                    disabledReason={conflict.reason}
                                                                />
                                                            );
                                                        })}
                                                </div>
                                            </div>

                                            {/* Show More ODDS */}
                                            {isSpecial && (overs.length > specialLimit || unders.length > specialLimit) && (
                                                <button
                                                    className="btn-more"
                                                    onClick={() =>
                                                        setShowLimit(prev => ({
                                                            ...prev,
                                                            [specialLimitKey]: (prev[specialLimitKey] ?? 5) + 5
                                                        }))
                                                    }
                                                >
                                                    Show More
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={key} className={isSpecial ? "container-special-item" : ""}>
                                    {(isSpecial ? list.slice(0, specialLimit) : list).map(odd => {
                                        const bet = bets?.find(b => b.id_selection === odd.id);
                                        const conflict = getConflictState(odd, game.id);

                                        return (
                                            <div key={odd.id} className={isSpecial ? "odd-special-item" : `props-data-container-defaults${propsLive ? " props-live-row props-live-row-single" : ""}`}>
                                                <div className={isSpecial ? "team-future" : ""}>
                                                    <span className="name-odd-props-defaul">
                                                        {odd.name}
                                                    </span>
                                                </div>

                                                <div className={isSpecial ? "" : "props-odd-container"} style={isSpecial ? { width: "25%" } : undefined}>
                                                    <OddDisplay
                                                        o={odd}
                                                        currentBet={bet}
                                                        onRequestBetsModal={onRequestBetsModal}
                                                        isSpecial={isSpecial}
                                                        propsLive={propsLive}
                                                        disabled={conflict.disabled}
                                                        disabledReason={conflict.reason}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Show More DEFAULT ODDS */}
                                    {isSpecial && list.length > specialLimit && (
                                        <button
                                            className="btn-more"
                                            onClick={() =>
                                                setShowLimit(prev => ({
                                                    ...prev,
                                                    [specialLimitKey]: (prev[specialLimitKey] ?? 5) + 5
                                                }))
                                            }
                                        >
                                            Show More
                                        </button>
                                    )}
                                </div>
                            );
                        })}

                        {/* how More GROUPS */}
                        {groupEntries.length > groupLimit && (
                            <button
                                className="btn-more"
                                onClick={() =>
                                    setShowLimit(prev => ({
                                        ...prev,
                                        [groupLimitKey]: (prev[groupLimitKey] ?? 5) + 5
                                    }))
                                }
                            >
                                Show More
                            </button>
                        )}

                    </React.Fragment>
                );
            })}
        </>
    );
}


